/**
 * 일일보고서 축소 관문 — 저장이 그 날짜의 전표를 크게 지워버리는 것을 막는다.
 *
 * ■ 실제로 겪은 사고 (2026-09-04 전수 조사에서 발견)
 * 업로드는 파일 종류를 구분하지 않고, 파일이 언급한 **모든 진료일**의 문서를 통째로 갈아끼운다.
 * 미수금 수납은 진료일이 과거이므로, 7월에 올린 수납통계가 6월 문서를 열어 그날의 전표를
 * 전부 지우고 0원짜리 한 줄만 남겼다.
 *
 *   2026-06-04 : 27장 14,959,980원 → 1장 0원
 *
 * 이렇게 일곱 달(2025-10 · 2026-01~06)에서 223,197,180원어치 명세가 사라졌다.
 * 월누계(monthlyReceipt)에만 총액이 남아 화면은 정상으로 보였고, 아무도 몰랐다.
 *
 * ■ 왜 '파일 종류 판별'이 아니라 '축소 감지'인가
 * 파일명·컬럼으로 수납통계를 골라내는 방어는 형식이 바뀌면 뚫린다 — 2026-08 export가
 * '어시스트'를 '담당직원'으로 바꿔 어시스트 파싱이 통째로 무너졌던 전례가 있다.
 * 값이 크게 줄어드는 쓰기 자체를 막으면 파일이 무엇이든, 형식이 어떻게 바뀌든 걸린다.
 *
 * ■ 왜 '병합'이 아닌가
 * 엑셀 재업로드가 이 시스템의 유일한 정정 수단이다(8·9월을 실제로 그렇게 고쳤다).
 * 병합하면 취소·수정으로 사라져야 할 전표가 영영 남는다.
 */

export interface DailyReceiptSnapshot {
  count: number;
  amount: number;
}

export interface ShrinkVerdict {
  /** 저장을 보류해야 하는가 */
  blocked: boolean;
  before: DailyReceiptSnapshot;
  after: DailyReceiptSnapshot;
  /** 줄어든 금액 (늘었으면 음수) */
  amountDrop: number;
  /** 사용자에게 보여줄 한 줄 — 보류가 아니면 빈 문자열 */
  summary: string;
}

/** 전표 목록에서 건수·금액을 뽑는다. 목록이 없으면 0/0. */
export const summarizeReceipts = (items: any): DailyReceiptSnapshot => {
  if (!Array.isArray(items)) return { count: 0, amount: 0 };
  return {
    count: items.length,
    amount: items.reduce((sum: number, item: any) => sum + (Number(item?.amount) || 0), 0),
  };
};

/**
 * 기존 저장분과 새로 쓸 분을 비교해 보류 여부를 판정한다.
 *
 * 보류 조건 — 셋을 모두 만족할 때만:
 *   · 기존 전표가 있다 (첫 저장은 비교 대상이 없다)
 *   · 금액이 줄어든다 (전표 수만 줄고 금액이 같으면 중복 정리이지 소실이 아니다)
 *   · 전표 수가 **절반 이하**로 준다
 *
 * ■ 왜 금액 임계를 쓰지 않는가 (정책 결정 2026-09-06)
 * 처음에는 '100만원 이상 감소'도 보류 조건에 넣었다. 그런데 환자 한 명이 300만원을 취소하는
 * 것은 정상 업무다 — 그걸 매번 확인창으로 막으면 관문이 잔소리가 되고, 결국 확인을 습관적으로
 * 누르게 된다.
 *
 * 실제 사고 39건을 전수 조사하니 남은 전표가 1장(37건)·2장(1건)·3장(1건)이었고 금액은 전부
 * 0원이었다. 정상 문서는 중앙값 30장(최대 49장)이다. 겹치는 구간이 없다.
 * 300만원 취소는 30장 → 29장이라 통과한다. **믿을 신호는 금액이 아니라 행 수 붕괴다.**
 *
 * 행 수는 그대로인데 금액만 무너지는 모양은 관측된 적이 없고, 그 경우는 월 단위 게이트가 잡는다.
 */
export const assessDailyShrink = (
  date: string,
  before: DailyReceiptSnapshot,
  after: DailyReceiptSnapshot
): ShrinkVerdict => {
  const amountDrop = before.amount - after.amount;
  const hadData = before.count > 0;
  const lostMoney = amountDrop > 0;
  const halvedRows = after.count * 2 <= before.count;

  const blocked = hadData && lostMoney && halvedRows;

  return {
    blocked,
    before,
    after,
    amountDrop,
    summary: blocked
      ? `${date}: 전표 ${before.count}장 ${before.amount.toLocaleString()}원 → ` +
        `${after.count}장 ${after.amount.toLocaleString()}원 (−${amountDrop.toLocaleString()}원)`
      : '',
  };
};

/** 보류된 날짜들을 사용자에게 한 번에 묻기 위한 문구 */
export const buildShrinkConfirmMessage = (verdicts: ShrinkVerdict[], dates: string[]): string =>
  `⚠️ 아래 ${verdicts.length}개 날짜는 이번 업로드로 전표가 크게 줄어듭니다.\n\n` +
  verdicts.map((v) => `  · ${v.summary}`).join('\n') +
  `\n\n다른 종류의 파일(예: 수납통계)이 과거 진료일을 건드리면 그날 명세가 통째로 지워집니다.\n` +
  `그래도 이 ${dates.length}개 날짜를 덮어쓰시겠습니까?\n` +
  `(취소하면 이 날짜들만 건너뛰고 나머지는 정상 저장됩니다)`;


/**
 * 명세가 무너진 날 — 전표가 몇 장 안 남고 금액이 0원인 날.
 *
 * 사고 39건 실측: 남은 전표 1장(37) · 2장(1) · 3장(1), 금액 전부 0원.
 * 정상 문서는 중앙값 30장(최대 49장)이라 3장을 경계로 잡으면 겹치지 않는다.
 *
 * ⚠️ 완벽한 판정이 아니다. 진료가 없던 날(휴진일)도 같은 모양이다 — 실측 42건 중 3건이
 *    그런 날이었다(2025-07-05 · 2026-07-05 · 2026-07-18). 파일명으로는 갈리지만 그 방법은
 *    형식이 바뀌면 뚫린다(2026-08 export 가 '어시스트'를 '담당직원'으로 바꾼 전례).
 *
 *    그래서 오판을 감수한다. 두 실수의 무게가 다르기 때문이다(정책 결정 2026-09-06):
 *      · 틀려서 막음  → 그 달 월누계가 갱신되지 않음. 불편하지만 되돌릴 수 있다.
 *      · 틀려서 통과  → 월 총액이 무너짐. 원본 엑셀이 없으면 못 되돌린다.
 *    게다가 이 판정은 월 게이트가 발화한 뒤에만 도는데, 휴진일이 있는 두 달(2025-07·2026-07)은
 *    총액과 명세 차이가 0원이라 애초에 게이트가 발화하지 않는다.
 */
export const COLLAPSED_MAX_ROWS = 3;

export interface CollapsedDay {
  date: string;
  count: number;
}

/**
 * 그 날짜의 전표 목록이 '무너진' 모양인가.
 *
 * count > 0 을 요구한다 — 전표가 아예 없는 문서는 **아직 올리지 않은 날**이지 무너진 날이 아니다.
 * 실측 사고 39건은 전부 1장 이상(1·2·3장)이 남아 있었다. 이 조건이 없으면 오늘 날짜의 빈 문서가
 * 매번 걸려 **현재 달에 항상 경고가 뜬다**(실데이터 시뮬레이션에서 2026-09-07이 그렇게 잡혔다).
 */
export const isCollapsedDay = (items: any): boolean => {
  const { count, amount } = summarizeReceipts(items);
  return count > 0 && count <= COLLAPSED_MAX_ROWS && amount === 0;
};

/** 한 달치 문서에서 무너진 날을 골라낸다 (날짜 오름차순) */
export const findCollapsedDays = (
  docs: Array<{ date: string; items: any }>
): CollapsedDay[] =>
  docs
    .filter((d) => isCollapsedDay(d.items))
    .map((d) => ({ date: d.date, count: summarizeReceipts(d.items).count }))
    .sort((a, b) => a.date.localeCompare(b.date));

/** 차단 안내 문구 — 무엇이 막혔고 무엇을 해야 하는지 그 자리에서 말한다 */
export const buildCollapsedBlockMessage = (yearMonth: string, days: CollapsedDay[]): string =>
  `🛡️ ${yearMonth} 월별 집계를 저장하지 않았습니다.\n\n` +
  `이 달은 아래 ${days.length}일의 명세가 비어 있습니다(전표 ${days.map((d) => d.count).join('·')}장, 금액 0원):\n` +
  `  ${days.map((d) => d.date.slice(5)).join(', ')}\n\n` +
  `지금 재계산하면 월 총액이 이 빈 날들을 반영해 크게 줄어듭니다.\n` +
  `그 달의 진료비통계 엑셀을 다시 올려 명세를 되살린 뒤에 다시 시도해 주세요.\n` +
  `(월 총액은 현재 값 그대로 보존됩니다)`;
